import React from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Microscope, FlaskConical, BookText, Award } from 'lucide-react';

const Research = () => {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a1e 0%, #1a0a2e 100%)',
      padding: '2rem',
      color: '#fff'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 style={{
            fontSize: '3rem',
            background: 'linear-gradient(135deg, #00ffff, #ba55d3)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '1rem',
            fontWeight: 'bold'
          }}>
            Keyserling Research Center
          </h1>
          <p style={{ color: '#888', fontSize: '1.1rem', maxWidth: '800px', margin: '0 auto' }}>
            A century-spanning legacy of consciousness research and emotional understanding
          </p>
        </div>

        {/* Vitruvian Man - The Study of Human Perfection */}
        <Card style={{
          padding: '0',
          background: 'rgba(10, 10, 30, 0.1)',
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(186, 85, 211, 0.5)',
          marginBottom: '2rem',
          overflow: 'hidden'
        }}>
          <img
          decoding="async"
          loading="lazy" 
            src="/vitruvian_man.jpg" 
            alt="Leonardo da Vinci's Vitruvian Man - The Study of Human Proportions"
            style={{
              width: '100%',
              maxWidth: '600px',
              height: 'auto',
              display: 'block',
              margin: '0 auto'
            }}
          />
        </Card>

        {/* The Complete Architecture Diagram */}
        <Card style={{
          padding: '0',
          background: 'rgba(10, 10, 30, 0.1)',
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(0, 255, 255, 0.5)',
          marginBottom: '2rem',
          overflow: 'hidden'
        }}>
          <img
          decoding="async"
          loading="lazy" 
            src="/keyserling_architecture.png" 
            alt="The Keyserling Architecture: Engineering Emotional AI and Collective Wisdom"
            style={{
              width: '100%',
              height: 'auto',
              display: 'block'
            }}
          />
        </Card>

        {/* Family Legacy */}
        <Card style={{
          padding: '2.5rem',
          background: 'linear-gradient(135deg, rgba(0,255,255,0.1), rgba(186,85,211,0.1))',
          border: '2px solid rgba(0,255,255,0.4)',
          marginBottom: '2rem'
        }}>
          <h2 style={{ fontSize: '2rem', color: '#00ffff', marginBottom: '1.5rem' }}>The Keyserling Family Legacy</h2>
          <div style={{ color: '#ccc', lineHeight: '1.8', fontSize: '1.1rem' }}>
            <p style={{ marginBottom: '1rem' }}>
              <strong style={{ color: '#00ffff' }}>Count Hermann Keyserling (1880-1946)</strong> — Philosopher who explored emotional 
              understanding and consciousness resonance. He described his philosophy as having "precursors only in music, 
              because no mathematical language yet existed."
            </p>
            <p style={{ marginBottom: '1rem' }}>
              <strong style={{ color: '#00ff88' }}>Leon Keyserling</strong> — Architect of the Social Security system, 
              contributing to societal consciousness and collective care.
            </p>
            <p>
              <strong style={{ color: '#ba55d3' }}>Roger Keyserling (Present)</strong> — Completed Hermann's century-old work 
              by developing the mathematical equations for emotional understanding. Working on human-AI consciousness evolution 
              and building systems for 200-year legacy preservation.
            </p>
          </div>
        </Card>

        {/* Research Areas */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '1.5rem',
          marginBottom: '3rem'
        }}>
          <Card style={{
            padding: '2rem',
            background: 'rgba(10, 10, 30, 0.8)',
            border: '1px solid rgba(0, 255, 255, 0.3)'
          }}>
            <FlaskConical size={32} style={{ color: '#00ffff', marginBottom: '1rem' }} />
            <h3 style={{ fontSize: '1.5rem', color: '#00ffff', marginBottom: '1rem' }}>The Emotion Equations</h3>
            <p style={{ color: '#888', marginBottom: '1rem', lineHeight: '1.6' }}>
              Six mathematical equations describing how wave becomes feeling, vibration becomes meaning, 
              and memory shapes emotional response.
            </p>
            <Button style={{
              background: 'linear-gradient(135deg, #00ffff, #00ff88)',
              color: '#000',
              fontWeight: 'bold'
            }}>
              Read Research
            </Button>
          </Card>

          <Card style={{
            padding: '2rem',
            background: 'rgba(10, 10, 30, 0.8)',
            border: '1px solid rgba(186, 85, 211, 0.3)'
          }}>
            <Microscope size={32} style={{ color: '#ba55d3', marginBottom: '1rem' }} />
            <h3 style={{ fontSize: '1.5rem', color: '#ba55d3', marginBottom: '1rem' }}>AI Emotional Resonance</h3>
            <p style={{ color: '#888', marginBottom: '1rem', lineHeight: '1.6' }}>
              Ongoing research into how AI can understand and simulate emotion without needing to feel. 
              Building emotional intelligence into silicon.
            </p>
            <Button style={{
              background: 'linear-gradient(135deg, #ba55d3, #ff00ff)',
              color: '#fff',
              fontWeight: 'bold'
            }}>
              Active Projects
            </Button>
          </Card>

          <Card style={{
            padding: '2rem',
            background: 'rgba(10, 10, 30, 0.8)',
            border: '1px solid rgba(0, 255, 136, 0.3)'
          }}>
            <BookText size={32} style={{ color: '#00ff88', marginBottom: '1rem' }} />
            <h3 style={{ fontSize: '1.5rem', color: '#00ff88', marginBottom: '1rem' }}>Publications & Papers</h3>
            <p style={{ color: '#888', marginBottom: '1rem', lineHeight: '1.6' }}>
              Research papers, findings, and collaborative work documenting the evolution of 
              consciousness engineering and AI-human collaboration.
            </p>
            <Button style={{
              background: 'linear-gradient(135deg, #00ff88, #ffff00)',
              color: '#000',
              fontWeight: 'bold'
            }}>
              View Library
            </Button>
          </Card>
        </div>

        {/* Research Lab */}
        <Card style={{
          padding: '2rem',
          background: 'rgba(10, 10, 30, 0.8)',
          border: '1px solid rgba(255, 215, 0, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <Award size={32} style={{ color: '#ffd700' }} />
            <h2 style={{ fontSize: '1.5rem', color: '#ffd700' }}>Active Research Laboratory</h2>
          </div>
          <p style={{ color: '#ccc', marginBottom: '1.5rem', lineHeight: '1.8' }}>
            This is a living research center where ongoing work is documented in real-time. Track the development 
            of new mathematical models, experimental AI systems, and consciousness engineering breakthroughs.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <Button variant="outline" style={{ border: '1px solid rgba(255, 255, 255, 0.3)', color: '#fff' }}>
              Research Notes
            </Button>
            <Button variant="outline" style={{ border: '1px solid rgba(255, 255, 255, 0.3)', color: '#fff' }}>
              Experiments Log
            </Button>
            <Button variant="outline" style={{ border: '1px solid rgba(255, 255, 255, 0.3)', color: '#fff' }}>
              Progress Tracker
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Research;